import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Tag, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
}

function renderContent(content) {
  return content.split('\n').map((line, i) => {
    if (!line.trim()) return <div key={i} className="h-3" />;
    if (line.startsWith('**') && line.endsWith('**')) {
      return <h3 key={i} className="font-outfit font-bold text-white text-xl mt-8 mb-3">{line.slice(2, -2)}</h3>;
    }
    return <p key={i} className="text-white/65 font-inter-tight text-base leading-relaxed">{line}</p>;
  });
}

export default function ResearchDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/articles/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="pt-24 flex items-center justify-center min-h-screen">
        <Loader2 className="text-bvx-cyan animate-spin" size={32} />
      </main>
    );
  }

  if (error || !article) {
    return (
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-20 max-w-7xl">
        <p className="text-white/50 font-inter-tight">Article not found.</p>
        <Link to="/research" className="text-bvx-cyan text-sm mt-4 inline-flex items-center gap-2 hover:gap-3 transition-all duration-200">
          <ArrowLeft size={14} /> Back to Research
        </Link>
      </main>
    );
  }

  const categoryColors = {
    Neuroscience: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    Nanotechnology: 'text-bvx-green bg-bvx-green/10 border-bvx-green/20',
    Research: 'text-bvx-cyan bg-bvx-cyan/10 border-bvx-cyan/20',
  };

  return (
    <main className="pt-24">
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <Link to="/research" className="inline-flex items-center gap-2 text-white/40 hover:text-bvx-cyan text-sm font-inter-tight transition-colors duration-200 mb-8" data-testid="back-to-research">
              <ArrowLeft size={14} /> Back to Research
            </Link>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
            <span className={`text-xs font-rajdhani font-semibold uppercase tracking-widest px-3 py-1 rounded-full border ${categoryColors[article.category] || categoryColors.Research}`}>
              {article.category}
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 mb-5 leading-tight"
            data-testid="article-title"
          >
            {article.title}
          </motion.h1>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="flex flex-wrap items-center gap-5 text-white/40 text-sm font-inter-tight mb-6 pb-6 border-b border-bvx-border"
          >
            <span className="flex items-center gap-1.5"><User size={13} /> {article.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(article.created_at)}</span>
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            className="text-white/70 font-inter-tight text-lg leading-relaxed mb-8 italic border-l-2 border-bvx-cyan pl-5"
          >
            {article.excerpt}
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={5}
            className="space-y-2 mb-10"
          >
            {renderContent(article.content)}
          </motion.div>

          {article.tags.length > 0 && (
            <motion.div
              variants={fadeUp} initial="hidden" animate="visible" custom={6}
              className="flex flex-wrap gap-2 pt-8 border-t border-bvx-border"
            >
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1 rounded-full font-inter-tight flex items-center gap-1">
                  <Tag size={10} /> {tag}
                </span>
              ))}
            </motion.div>
          )}

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={7}
            className="mt-12 glass-card p-6"
          >
            <p className="text-white/55 font-inter-tight text-sm mb-3">Published by Biovex Technologies Research Division</p>
            <Link to="/research" className="inline-flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200" data-testid="back-to-research-bottom">
              <ArrowLeft size={14} /> View All Articles
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
